// apps/web/src/features/commercial-proposals/calc.ts
//
// @aiPurpose pure PREVIEW-only re-implementation of the authoritative
//   commercial-proposal calculation (#1198). The backend (#1195) computes
//   the canonical totals with Decimal + ROUND_HALF_UP and the detail
//   response carries them; this client calc exists ONLY to update the
//   editor preview as the operator types, before a save round-trips. The
//   moment a draft is saved/published, the UI renders the backend
//   `calculation` instead of this output.
// @aiInvariant the formula and rounding MUST match the contract documented
//   on `CommercialProposalCalculationOutputSchema` so the live preview
//   reconciles to the backend values after save. `round2` is ROUND_HALF_UP
//   on the 2nd decimal; `grandTotalRub` rounds the SUM OF UNROUNDED row
//   totals (NOT the sum of already-rounded rows). It is pinned against the
//   shared golden fixtures by the unit test.
import type { CommercialProposalCalculationInput } from '@magimp/shared';

/**
 * ROUND_HALF_UP to 2 decimals, epsilon-safe against binary-float
 * representation error (e.g. `1.005 * 100 === 100.49999…`). Scale, nudge by
 * a tiny epsilon proportional to the magnitude so a true half lands on the
 * up side, then floor. Returns a finite number; the caller formats it as a
 * 2dp string.
 */
export function round2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const scaled = Math.abs(value) * 100;
  // Epsilon proportional to the scaled magnitude (never below a floor) so
  // it absorbs float error without flipping genuinely-below-half values.
  const epsilon = Math.max(scaled, 1) * Number.EPSILON * 4;
  return (sign * Math.floor(scaled + 0.5 + epsilon)) / 100;
}

export interface CommercialProposalLiveCalc {
  paymentDivisor: number;
  serviceCny: number;
  totalCny: number;
  buyRub: number;
  transportUsd: number;
  insuranceUsd: number;
  logisticsUsd: number;
  logisticsRub: number;
  grandTotalRub: number;
}

function paymentDivisor(input: CommercialProposalCalculationInput): number {
  if (input.paymentScheme === 'vat') return input.vatDivisor || 0.8;
  if (input.paymentScheme === 'novat') return input.noVatDivisor || 0.9;
  // cash / card / crypto -> no markup.
  return 1;
}

/**
 * Live preview calc. Mirrors the canonical formula exactly; money rows are
 * rounded with `round2` (ROUND_HALF_UP) while `grandTotalRub` rounds the
 * UNROUNDED row-total sum so it reconciles to the backend.
 */
export function calculateCommercialProposalPreview(
  input: CommercialProposalCalculationInput,
): CommercialProposalLiveCalc {
  // Mode-gating mirrors the backend engine (#1410): LOGISTICS drops the service
  // markup so totalCny collapses to goods only (insurance basis = goods, BR-7);
  // BUYOUT zeroes the logistics rows. BOTH is unchanged.
  const buyoutActive = input.mode !== 'LOGISTICS';
  const logisticsActive = input.mode !== 'BUYOUT';

  const divisor = paymentDivisor(input);
  const serviceCny =
    logisticsActive && !buyoutActive
      ? 0
      : ((input.goodsCny + input.chinaDeliveryCny) * input.servicePercent) / 100;
  const totalCny =
    logisticsActive && !buyoutActive
      ? input.goodsCny
      : input.goodsCny + input.chinaDeliveryCny + serviceCny;
  const buyRubFull = buyoutActive ? (totalCny * input.cnyRateRub) / divisor : 0;

  const transportUsd = logisticsActive
    ? (input.tariffUnit === 'm3' ? input.volumeM3 : input.weightKg) * input.tariffUsdPerKg
    : 0;
  const insuranceUsd = logisticsActive
    ? (totalCny / input.insuranceCnyPerUsdRate) * (input.insurancePercent / 100)
    : 0;
  const logisticsUsd = logisticsActive
    ? transportUsd + insuranceUsd + input.packagingUsd + input.prrUsd
    : 0;
  const logisticsRubFull = logisticsActive ? (logisticsUsd * input.usdRateRub) / divisor : 0;

  return {
    paymentDivisor: round2(divisor),
    serviceCny: round2(serviceCny),
    totalCny: round2(totalCny),
    buyRub: round2(buyRubFull),
    transportUsd: round2(transportUsd),
    insuranceUsd: round2(insuranceUsd),
    logisticsUsd: round2(logisticsUsd),
    logisticsRub: round2(logisticsRubFull),
    // INVARIANT: round the SUM OF UNROUNDED rows, never the rounded rows.
    grandTotalRub: round2(buyRubFull + logisticsRubFull),
  };
}
